/**
 * 大会の定数（企画書 §17 / §27-8、[ADR-023](../../../../docs/adr/023-tournament-progression.md)）。
 *
 * **値の正はここだけ。** マイグレーションにも API にも数字を書かない。
 */
/**
 * 大会の種類（[ADR-032](../../../../docs/adr/032-world-cup-and-ugc-safety.md)、#254）。
 *
 * | 種類    | 名前           | 出る選手                                 | 順位ポイント   | 試合の勝敗                 |
 * | ------- | -------------- | ---------------------------------------- | -------------- | -------------------------- |
 * | `CPU`   | CPUカップ      | 自分の選手 1 体 + CPU 7 体（ADR-023）    | **数えない**   | **練習の戦績**（`cpu_*`）  |
 * | `WORLD` | ワールドカップ | 受付中に入った人の選手 + 空いた枠の CPU  | 数える         | 公式の戦績                 |
 *
 * `REGIONAL` / `NATIONAL` は企画書 §17.1 の段階構造（地域予選 → 全国大会 → 世界大会）の名残で、作らない
 * （日本のみの提供で「地域予選」に意味が無い。ADR-023）。
 *
 * **クライアントは知らない値を受け取っても壊れないこと**（iOS の `TournamentType` は `RawRepresentable` の struct）。
 */
export const TOURNAMENT_TYPES = ['REGIONAL', 'NATIONAL', 'WORLD', 'CPU'];
/**
 * 大会の進行状態。
 *
 * `RUNNING` はユーザーがログインしていなくても進む（企画書 §17.2）。これが再訪動機の中心なので、
 * **クライアントの起動を前提にした進行にはしない。**
 */
export const TOURNAMENT_STATUSES = ['ENTRY', 'RUNNING', 'FINISHED'];
/** MVP の参加枠（企画書 §27-8「8 人程度」）。2 のべき乗であること。 */
export const TOURNAMENT_ENTRY_COUNT = 8;
/**
 * CPUカップの受付の窓（秒）。**0。** 他の人は入らないので待つ理由が無く、出た瞬間に始まる（ADR-023）。
 */
export const TOURNAMENT_ENTRY_WINDOW_SECONDS = 0;
/**
 * ワールドカップの受付の窓（秒）。**3 分**（ADR-032 B1）。最初の人が入ったときから数え、過ぎたら空いた枠を CPU で埋めて始める。
 * 8 人そろえば窓を待たずに始める。
 *
 * 子どもは待てないので短くした。人が少ないうちはほとんど CPU で埋まる（ADR-032 の Risks）。
 */
export const WORLD_CUP_ENTRY_WINDOW_SECONDS = 180;
/**
 * ラウンドとラウンドの間隔（秒）。**3 分**（#229。はじめは 10 分）。決勝まで約 6 分。
 *
 * **見て楽しむ体験を優先した**（2026-09-24、ユーザーの判断）。大会の試合は、画面を開いていれば自動で見る（#230）。
 * 10 分だと、見ている子が次の試合まで待てない。
 *
 * 代償（承知のうえ）:
 *
 * - **§37-4「結果を見に戻ってくるか」の検証には弱くなる。** 10 分にしていたのは「エントリーした画面でそのまま
 *   決着がつくなら戻ってくる理由が無い」ため。3 分だと、エントリーした画面で最後まで見終わりやすい
 * - **1 日に出られる回数が増え、ポイントを稼ぎやすくなる**（ADR-023 §8。参加コストはインク #65 で見直す）
 *
 * 変えると、**これから始まる大会だけ**が変わる（`scheduledAt` は大会を始めたときに決まる）。
 */
export const TOURNAMENT_ROUND_INTERVAL_SECONDS = 180;
/**
 * 順位ポイントの配点（[ADR-012](../../../../docs/adr/012-ranking-by-tournament-points.md) の宿題、
 * 配点は ADR-023）。
 *
 * 添字は**到達ラウンド**（1 = 1 回戦敗退、`TOURNAMENT_ENTRY_COUNT` が 8 なら 3 = 優勝）。
 *
 * **参加すれば必ず正の点が入る。** 対象は子どもで、出ただけで 0 点は罰に見える
 * （企画書 §40-5「キャラクターを使い捨てにしない」）。
 */
export const TOURNAMENT_POINTS = {
    /** 優勝。 */
    champion: 100,
    /** 準優勝（決勝で負けた）。 */
    runnerUp: 60,
    /** ベスト 4（準決勝で負けた）。 */
    semiFinal: 35,
    /** 1 回戦で負けた。**出ただけでも入る点。** */
    firstRound: 15,
};
/**
 * 順位ポイントを出す。
 *
 * @param reachedRound 最後に戦ったラウンド（1 = 1 回戦）
 * @param isChampion 決勝に勝ったか
 *
 * **`TOURNAMENT_ENTRY_COUNT` が 8（3 ラウンド）であることを前提にしている。** 枠を増やすときは
 * 配点表ごと引き直す（ADR-012 は「大会の形が決まった時点で必ず引き直す」と書いている）。
 */
export function tournamentPoints(reachedRound, isChampion) {
    if (isChampion)
        return TOURNAMENT_POINTS.champion;
    switch (reachedRound) {
        case 3:
            return TOURNAMENT_POINTS.runnerUp;
        case 2:
            return TOURNAMENT_POINTS.semiFinal;
        default:
            // 1 回戦敗退。**0 にしない**（出ただけで 0 点は子どもには罰に見える）。
            return TOURNAMENT_POINTS.firstRound;
    }
}
/**
 * 参加枠から決勝までのラウンド数を出す。8 人なら 3。
 *
 * **`battles.roundCount` と混同しないこと。** あちらは 1 試合の中のラウンド数である。
 */
export function tournamentTotalRounds(entryCount) {
    return Math.log2(entryCount);
}
